import { useParams, Link, useNavigate } from 'react-router-dom';
import PageBanner from '../components/PageBanner';
import PageTransition from '../components/PageTransition';
import materialsData from '../data/materials.json';
import useScrollTop from '../hooks/useScrollTop';
import { FaEnvelope } from 'react-icons/fa';
import '../styles/ProductDetailPage.css';

const ProductDetailPage = () => {
  useScrollTop();
  const navigate = useNavigate();
  const { categoryId, productId } = useParams();
  const category = materialsData.categories.find(cat => cat.id === categoryId);
  const product = category?.materials.find(mat => mat.id === productId);

  // Function to get related products from other categories
  const getRelatedProducts = () => {
    const relatedProducts = [];
    materialsData.categories.forEach(cat => {
      // Include products from the same category (except the current product)
      if (cat.id === categoryId) {
        const sameCategory = cat.materials.filter(material => material.id !== productId);
        relatedProducts.push(...sameCategory.slice(0, 4).map(material => ({
          ...material,
          categoryId: cat.id,
          categoryName: cat.name
        })));
      }
    });
    return relatedProducts; // Limit to 4 related products
  };

  return (
    <PageTransition>
      <div className="product-detail-page">
      <PageBanner 
        title={product.name}
        description={product.description}
      />
      
      <div className="breadcrumbs">
        <div className="breadcrumbs-content">
          <Link to="/" className="breadcrumb-item">Home</Link>
          <span className="breadcrumb-separator">/</span>
          <Link to={`/category/${categoryId}`} className="breadcrumb-item">{category.name}</Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">{product.name}</span>
        </div>
      </div>

      <div className="product-detail-container">
        <div className="product-images">
          <div className="main-image">
            <img src={product.imageUrl} alt={product.name} />
          </div>
        </div>

        <div className="product-info">
          <h2>{product.name}</h2>
          <p className="product-description">{product.description}</p>

          {product.features && product.features.length > 0 && (
            <div className="features-section">
              <h3>Key Features</h3>
              <ul className="features-list">
                {product.features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>
          )}

          {product.specifications && Object.keys(product.specifications).length > 0 && (
            <div className="specifications">
              <h3>Specifications</h3>
              <table className="specs-table">
                <tbody>
                  {Object.entries(product.specifications).map(([key, value]) => {
                    if (typeof value === 'object') {
                      return Object.entries(value).map(([subKey, subValue]) => (
                        <tr key={`${key}-${subKey}`}>
                          <td>{`${key} - ${subKey}`}</td>
                          <td>{subValue}</td>
                        </tr>
                      ));
                    }
                    return (
                      <tr key={key}>
                        <td>{key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}</td>
                        <td>{value}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {product.commonUses && product.commonUses.length > 0 && (
            <div className="common-uses-section">
              <h3>Common Uses</h3>
              <ul className="common-uses-list">
                {product.commonUses.map((use, index) => (
                  <li key={index}>{use}</li>
                ))}
              </ul>
            </div>
          )}

          {product.packaging && (
            <div className="packaging-section">
              <h3>Packaging</h3>
              <p>{product.packaging}</p>
            </div>
          )}

          {product.storage && (
            <div className="storage-section">
              <h3>Storage</h3>
              <p>{product.storage}</p>
            </div>
          )}

          {product.safety && (
            <div className="safety-section">
              <h3>Safety Information</h3>
              <p className="safety-info">{product.safety}</p>
            </div>
          )}

          <button 
            className="inquire-btn" 
            onClick={() => navigate(`/contact?product=${encodeURIComponent(product.name)}&category=${encodeURIComponent(category.name)}`)}
          >
            <FaEnvelope size={24} />
            Inquire Now
          </button>
        </div>
      </div>

      <div className="related-products-section">
        <h2>Related Products</h2>
        <div className="related-products-grid">
          {getRelatedProducts().map((relatedProduct) => (
            <div className="related-product-card" key={`${relatedProduct.categoryId}-${relatedProduct.id}`}>
              <div className="related-product-image">
                <img src={relatedProduct.imageUrl} alt={relatedProduct.name} />
              </div>
              <div className="related-product-content">
                <h3>{relatedProduct.name}</h3>
                <p className="category-name">{relatedProduct.categoryName}</p>
                <p className="description">{relatedProduct.description}</p>
                <Link
                  to={`/category/${relatedProduct.categoryId}/product/${relatedProduct.id}`}
                >
                  <button className="view-details-btn">View Details</button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    </PageTransition>
  );
};

export default ProductDetailPage;
